import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Col, Row, Table, Spinner, Badge, Button, Container } from 'react-bootstrap';
import axios from 'axios';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';
import Swal from 'sweetalert2';

const POAMView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [poData, setPoData] = useState(null);

    // Fetch PO data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`${BASE_URL}purchaseOrder/findById/${id}`, {
                    headers: authHeader()
                });
                setPoData(response.data);
            } catch (error) {
                console.error("Error fetching PO:", error);
                Swal.fire("Error", "Failed to fetch purchase order", "error");
                navigate("/poindex/1");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Draft': return 'secondary';
            case 'Submitted': return 'primary';
            case 'Approved': return 'success';
            case 'Rejected': return 'danger';
            default: return 'info';
        }
    };

    // Calculate totals
    const calculateTotals = () => {
        if (!poData) return { productsTotal: 0, chargesTotal: 0, grossTotal: 0, grandTotal: 0 };

        const productsTotal = poData.purchaseOrderItems?.reduce(
            (sum, item) => sum + (item.totalinr || 0), 0
        ) || 0;

        const chargesTotal = poData.additionalCharges?.reduce(
            (sum, charge) => sum + (charge.chargetotal || 0), 0
        ) || 0;

        const grossTotal = productsTotal + chargesTotal;
        const grandTotal = Math.round(grossTotal);

        return { productsTotal, chargesTotal, grossTotal, grandTotal };
    };

    const { productsTotal, chargesTotal, grossTotal, grandTotal } = calculateTotals();

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(value || 0);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!poData) {
        return <div className="alert alert-danger text-center mt-4">Purchase order not found</div>;
    }

    return (
        <Container fluid className="p-3">
            {/* Header Section */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="fw-bold text-primary mb-0">PURCHASE ORDER - VIEW</h5>
                        <div className="d-flex gap-2">
                            <Button
                                // variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/Purchses/PurchaseOrder/AraiManufacturing/PoDomPrint/${id}`)}
                            >
                                <FaPrint className="me-1" />
                                PRINT
                            </Button>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                                <FaArrowLeft className="me-1" />
                                Back
                            </button>
                        </div>
                    </div>

                    <Row className="g-2">
                        <Col md={3}>
                            <div className="border-start border-3 border-primary ps-2">
                                <small className="text-muted">PO No</small>
                                <div className="fw-bold">{poData.pono}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-success ps-2">
                                <small className="text-muted">Date</small>
                                <div className="fw-bold">
                                    {new Date(poData.podate).toLocaleDateString('en-IN')}
                                </div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-warning ps-2">
                                <small className="text-muted">Job Number</small>
                                <div className="fw-bold">{poData.jobnumber}</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="border-start border-3 border-info ps-2">
                                <small className="text-muted">Status</small>
                                <div>
                                    <Badge
                                        bg={getStatusBadge(poData.status)}
                                        className="px-2 py-1"
                                    >
                                        {poData.status}
                                    </Badge>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* PO Basic Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">PO Information</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Enquiry No</small>
                                <div className="fw-bold">{poData.enquiryno || '-'}</div>
                            </div>
                            <div className="mb-2">
                                <small className="text-muted">Type</small>
                                <div className="fw-bold">{poData.potype}</div>
                            </div>
                            <div>
                                <small className="text-muted">Delivery Terms</small>
                                <div className="fw-bold">{poData.deliveryterm || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Enquiry Date</small>
                                <div className="fw-bold">
                                    {poData.enquirydate ? new Date(poData.enquirydate).toLocaleDateString('en-IN') : '-'}
                                </div>
                            </div>
                            <div className="mb-2">
                                <small className="text-muted">Ship To</small>
                                <div className="fw-bold">{poData.shipto || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Delivery Time</small>
                                <div className="fw-bold">{poData.deliverytime || '-'}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Certificate</small>
                                <div className="fw-bold">{poData.certificate || '-'}</div>
                            </div>
                            <div className="mb-2">
                                <small className="text-muted">Quotation Ref No</small>
                                <div className="fw-bold">{poData.quotationreferencenumber || '-'}</div>
                            </div>
                            <div>
                                <small className="text-muted">Quotation Ref Date</small>
                                <div className="fw-bold">{poData.quotationreferencedate || '-'}</div>
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="mt-2">
                                <small className="text-muted">Payment Terms</small>
                                <div className="fw-bold">{poData.paymentterms || 'Not specified'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Supplier Information */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Supplier Details</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Company Name</small>
                                <div className="fw-bold">{poData.supplier?.companyname}</div>
                            </div>
                            <div>
                                <small className="text-muted">Contact Person</small>
                                <div className="fw-bold">{poData.contactperson}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="mb-2">
                                <small className="text-muted">Phone</small>
                                <div className="fw-bold">{poData.contactno}</div>
                            </div>
                            <div>
                                <small className="text-muted">Email</small>
                                <div className="fw-bold">{poData.email}</div>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div>
                                <small className="text-muted">Address</small>
                                <div className="fw-bold">
                                    {poData.supplierMultipleAddress?.supplieraddress}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Branch & Warehouse */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Branch & Warehouse</h6>
                </Card.Header>
                <Card.Body className="p-3">
                    <Row>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Branch</small>
                                <div className="fw-bold">{poData.branch?.branchname || '-'}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="mb-2">
                                <small className="text-muted">Warehouse</small>
                                <div className="fw-bold">{poData.warehouse?.warehousename || '-'}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Products Table */}
            <Card className="shadow-sm border-0 rounded mb-3">
                <Card.Header className="bg-light py-2">
                    <h6 className="mb-0 fw-bold">Product Details</h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-center fw-bold py-2">#</th>
                                    <th className="fw-bold py-2">Description</th>
                                    <th className="fw-bold py-2">Part No</th>
                                    <th className="fw-bold py-2">HSN Code</th>
                                    <th className="fw-bold py-2">MOC</th>
                                    <th className="fw-bold py-2">Grade Of Material</th>
                                    <th className="fw-bold py-2">Type</th>
                                    <th className="fw-bold py-2">Size</th>
                                    <th className="fw-bold py-2 text-center">Qty</th>
                                    <th className="fw-bold py-2 text-center">UOM</th>
                                    <th className="fw-bold py-2 text-end">Unit Price</th>
                                    <th className="fw-bold py-2 text-center">GST %</th>
                                    <th className="fw-bold py-2 text-end">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {poData.purchaseOrderItems?.map((item, index) => (
                                    <tr key={index}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                            <div>{item.product.description}</div>
                                            {item.productremarks && (
                                                <div className="text-muted small">
                                                    <small>Remarks: {item.productremarks}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>{item.partno || '-'}</td>
                                        <td>{item.hsnsacno || '-'}</td>
                                        <td>{item.moc || '-'}</td>
                                        <td>{item.gradeofmaterial || '-'}</td>
                                        <td>{item.type || '-'}</td>
                                        <td>{item.size || '-'}</td>
                                        <td className="text-center">{item.quantity}</td>
                                        <td className="text-center">{item.uom}</td>
                                        <td className="text-end">{formatCurrency(item.unitrateinr)}</td>
                                        <td className="text-center">{item.gst}%</td>
                                        <td className="text-end fw-bold">{formatCurrency(item.totalinr)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <td colSpan="11" className="text-end fw-bold py-2">
                                        Products Total:
                                    </td>
                                    <td className="text-end fw-bold py-2">
                                        {formatCurrency(productsTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Additional Charges */}
            {poData.additionalCharges?.length > 0 && (
                <Card className="shadow-sm border-0 rounded mb-3">
                    <Card.Header className="bg-light py-2">
                        <h6 className="mb-0 fw-bold">Additional Charges</h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table bordered size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="fw-bold py-2">#</th>
                                        <th className="fw-bold py-2">Charge Type</th>
                                        <th className="fw-bold py-2 text-end">Amount</th>
                                        <th className="fw-bold py-2 text-center">GST %</th>
                                        <th className="fw-bold py-2 text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {poData.additionalCharges.map((charge, index) => (
                                        <tr key={index}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{charge.chargetype}</td>
                                            <td className="text-end">{formatCurrency(charge.chargeamount)}</td>
                                            <td className="text-center">{charge.chargegst}%</td>
                                            <td className="text-end fw-bold">{formatCurrency(charge.chargetotal)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <td colSpan="4" className="text-end fw-bold py-2">
                                            Charges Total:
                                        </td>
                                        <td className="text-end fw-bold py-2">
                                            {formatCurrency(chargesTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Summary Section */}
            <Row>
                <Col lg={6} className="ms-auto">
                    <Card className="shadow-sm border-0 rounded">
                        <Card.Header className="bg-light py-2">
                            <h6 className="mb-0 fw-bold text-center">Purchase Order Summary</h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                            <div className="space-y-2">
                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Products Total:</span>
                                    <span className="fw-bold">{formatCurrency(productsTotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Additional Charges:</span>
                                    <span className="fw-bold">{formatCurrency(chargesTotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
                                    <span className="fw-semibold">Gross Total:</span>
                                    <span className="fw-bold">{formatCurrency(grossTotal)}</span>
                                </div>

                                <div className="d-flex justify-content-between align-items-center py-2 border-top">
                                    <span className="fw-bold">Grand Total (Rounded)</span>
                                    <span className="fw-bold text-primary">{formatCurrency(grandTotal)}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Footer Actions */}
            <div className="text-center mt-3 pt-2 border-top">
                <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-1" />
                    Back to List
                </button>
            </div>
        </Container>
    );
};

export default POAMView;